import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useActivityViewData } from "#app/hooks/issue-view/use-activity-view-data";
import { useNoteMutations } from "#app/hooks/issue-view/use-note-mutations";
import { useMarkNotesRead } from "#app/hooks/issue-view/use-mark-notes-read";
import { useAppData } from "#app/lib/data";
import type { ActivityContextValue } from "#app/components/issue-view/activity/activity-context";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";
import { useTransientValue } from "#app/lib/hooks/use-transient-value";

const HIGHLIGHT_DURATION = 3000;

export interface UseActivityContextValueResult {
  value: ActivityContextValue;
  loading: boolean;
  hasActivities: boolean;
  /** Loaded note threads (replies nested) — consumed by the media tab to
   * aggregate note photos without a second notes query. */
  notes: NoteNode[];
}

export function useActivityContextValue(
  issueId: string,
  focusNoteId?: string | null,
  autoScrollReady: boolean = true,
  focusUnread: boolean = false,
): UseActivityContextValueResult {
  const activityData = useActivityViewData(issueId);
  const noteMutations = useNoteMutations(issueId);
  const { markNoteRead } = useMarkNotesRead();
  const { currentUser } = useAppData();

  const [focusActivityId, setFocusActivityId] = useState<string | null>(null);
  const [focusNoteIdState, setFocusNoteIdState] = useState<string | null>(
    focusNoteId ?? null,
  );
  const clearFocusActivity = useCallback(() => {
    setFocusActivityId(null);
    setFocusNoteIdState(null);
  }, []);

  // The note a jump landed on, with its parent when it is a reply; both
  // clear together when the highlight fades.
  const [highlight, showHighlight] = useTransientValue<{
    noteId: string;
    parentId: string | null;
  }>(HIGHLIGHT_DURATION);
  const selectedNoteId = highlight?.noteId ?? null;
  const selectedParentId = highlight?.parentId ?? null;
  const noteRefsMap = useRef<Map<string, any>>(new Map());

  // Refs that always hold the latest pagination state. scrollToNote runs an
  // async loop that loads pages until the target note appears; it must read
  // the latest hasMoreActivities and call the latest loadMoreActivities each
  // iteration, otherwise it captures stale closures and re-fetches the same
  // page forever.
  const hasMoreRef = useRef(activityData.hasMoreActivities);
  hasMoreRef.current = activityData.hasMoreActivities;
  const loadMoreRef = useRef(activityData.loadMoreActivities);
  loadMoreRef.current = activityData.loadMoreActivities;

  const registerNoteRef = useCallback((noteId: string, el: any) => {
    if (el) noteRefsMap.current.set(noteId, el);
    else noteRefsMap.current.delete(noteId);
  }, []);
  const getNoteElement = useCallback(
    (noteId: string): HTMLElement | null => noteRefsMap.current.get(noteId) ?? null,
    [],
  );

  const scrollToNote = useCallback(
    async (noteId: string, parentNoteId?: string | null) => {
      showHighlight({ noteId, parentId: parentNoteId ?? null });

      const tryScroll = () => {
        const el = noteRefsMap.current.get(noteId);
        if (el) {
          el.scrollIntoView?.({ behavior: "smooth", block: "center" });
          return true;
        }
        return false;
      };

      if (tryScroll()) return;

      for (let i = 0; i < 10; i++) {
        await new Promise((r) => requestAnimationFrame(r));
        if (tryScroll()) return;
      }

      while (hasMoreRef.current) {
        await loadMoreRef.current();
        for (let i = 0; i < 3; i++) {
          await new Promise((r) => requestAnimationFrame(r));
          if (tryScroll()) return;
        }
      }

      await new Promise((r) => requestAnimationFrame(r));
      tryScroll();
    },
    [showHighlight],
  );

  // Destructured so the effect depends on the two things it reads rather than
  // on the whole activity bag, which changes on every fetch.
  const { activities, getNote } = activityData;

  // Unread snapshot, taken ONCE per issue when the notes query settles (a
  // cache-and-network read reports loading until the network answers, so the
  // flags are fresh). The feed marks notes read as they scroll into view; the
  // "New" marker must not chase that, so it reads this frozen set, never the
  // live `unread` flags. Reset when the hook is re-pointed at another issue
  // (the mobile sheet keeps one instance across opens).
  const [unreadAtOpen, setUnreadAtOpen] = useState<ReadonlySet<string> | null>(null);
  const snapshotIssueRef = useRef<string | null>(null);
  const { loading: notesSettling, notes } = activityData;
  useEffect(() => {
    if (snapshotIssueRef.current !== issueId) {
      snapshotIssueRef.current = issueId;
      setUnreadAtOpen(null);
    }
  }, [issueId]);
  useEffect(() => {
    if (unreadAtOpen || notesSettling || !issueId) return;
    const set = new Set<string>();
    for (const n of notes) {
      if (n.unread) set.add(n.noteId);
      for (const r of n.replies ?? []) if (r.unread) set.add(r.noteId);
    }
    setUnreadAtOpen(set);
  }, [unreadAtOpen, notesSettling, notes, issueId]);

  // The oldest snapshot note still present decides where "New" starts; a
  // reply names its parent so the thread can open on landing.
  const { firstUnreadNoteId, firstUnreadParentId } = useMemo(() => {
    type Candidate = { id: string; parent: string | null; at: number };
    const candidates: Candidate[] = [];
    if (unreadAtOpen?.size) {
      for (const n of notes) {
        if (unreadAtOpen.has(n.noteId)) {
          candidates.push({ id: n.noteId, parent: null, at: Date.parse(n.creationDate) });
        }
        for (const r of n.replies ?? []) {
          if (unreadAtOpen.has(r.noteId)) {
            candidates.push({ id: r.noteId, parent: n.noteId, at: Date.parse(r.creationDate) });
          }
        }
      }
    }
    const first = candidates.reduce<Candidate | null>(
      (oldest, c) => (!oldest || c.at < oldest.at ? c : oldest),
      null,
    );
    return {
      firstUnreadNoteId: first?.id ?? null,
      firstUnreadParentId: first?.parent ?? null,
    };
  }, [unreadAtOpen, notes]);

  const didAutoScroll = useRef(false);
  useEffect(() => {
    if (
      focusNoteId &&
      autoScrollReady &&
      !didAutoScroll.current &&
      activities.length > 0
    ) {
      didAutoScroll.current = true;
      const note = getNote(focusNoteId);
      scrollToNote(focusNoteId, note?.parentNoteId);
    }
  }, [focusNoteId, autoScrollReady, activities.length, getNote, scrollToNote]);

  const value = useMemo<ActivityContextValue>(
    () => ({
      activities: activityData.activities,
      getNote: activityData.getNote,
      loadMoreActivities: activityData.loadMoreActivities,
      hasMoreActivities: activityData.hasMoreActivities,
      loadingMore: activityData.loadingMore,
      activityDates: activityData.activityDates,
      scrollToNote,
      selectedNoteId,
      selectedParentId,
      registerNoteRef,
      getNoteElement,
      markNoteRead,
      createNote: noteMutations.createNote,
      updateNote: noteMutations.updateNote,
      deleteNote: noteMutations.deleteNote,
      toggleReaction: noteMutations.toggleReaction,
      currentUserId: currentUser?.userId ?? null,
      focusActivityId,
      focusNoteId: focusNoteIdState,
      clearFocusActivity,
      unreadAtOpen: unreadAtOpen ?? undefined,
      firstUnreadNoteId,
      firstUnreadParentId,
      // An explicit note focus outranks the landing; the sheet holds the
      // landing until its open transition has settled.
      landOnUnread: focusUnread && !focusNoteId && autoScrollReady,
    }),
    [
      activityData.activities, activityData.getNote, activityData.loadMoreActivities,
      activityData.hasMoreActivities, activityData.loadingMore, activityData.activityDates,
      scrollToNote,
      selectedNoteId, selectedParentId, registerNoteRef, getNoteElement, markNoteRead,
      noteMutations.createNote, noteMutations.updateNote, noteMutations.deleteNote,
      noteMutations.toggleReaction, currentUser?.userId,
      focusActivityId, focusNoteIdState, clearFocusActivity,
      unreadAtOpen, firstUnreadNoteId, firstUnreadParentId,
      focusUnread, focusNoteId, autoScrollReady,
    ],
  );

  return {
    value,
    loading: activityData.loading,
    hasActivities: activityData.activities.length > 0,
    notes: activityData.notes,
  };
}
